// =============================================================================
// MeuDriver — Instructor Edit Profile
// =============================================================================

import { useEffect, useState, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Save, Upload, X, Camera, MessageCircle, Instagram, Linkedin, Link as LinkIcon, Megaphone, Rss, Bell, Trash2, CalendarClock, ShieldCheck, Palmtree } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { InstructorCnhUploadCard } from '@/components/instructor/InstructorCnhUploadCard'
import { LgpdRequestForm } from '@/components/shared/LgpdRequestForm'
import { InstructorIdentityCard } from '@/components/instructor/InstructorIdentityCard'
import { InstructorIdentityDocCard } from '@/components/instructor/InstructorIdentityDocCard'
import { TwoFactorSetupCard } from '@/components/shared/TwoFactorSetupCard'
import { VacationModeCard } from '@/components/instructor/VacationModeCard'
import { InstructorTagsEditor } from '@/components/instructor/InstructorTagsEditor'
import { PushNotificationToggle } from '@/components/shared/PushNotificationToggle'
import { cn, formatCurrency, getStudentPrice } from '@/lib/utils'
import { SPECIALTY_LABELS, DAY_OF_WEEK_LABELS, BRAZILIAN_STATES, PLATFORM_FEE_PERCENT } from '@/lib/constants'
import type { InstructorRow, ProfileRow, SpecialtyType, AvailabilityRow } from '@/types/database'

// ---------------------------------------------------------------------------
// Zod schemas
// ---------------------------------------------------------------------------

const personalSchema = z.object({
  full_name: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  phone: z.string().optional(),
  bio: z.string().min(20, 'Bio deve ter pelo menos 20 caracteres').max(1000),
  years_experience: z.coerce.number().int().min(0).max(60),
  hourly_rate: z.coerce.number().min(20).max(500),
  service_radius_km: z.coerce.number().int().min(5).max(100),
  city: z.string().min(2, 'Informe a cidade'),
  state: z.string().length(2, 'Use a sigla do estado (2 letras)'),
  latitude: z.coerce.number().optional().or(z.literal('')),
  longitude: z.coerce.number().optional().or(z.literal('')),
  // Campos L1
  teaching_since: z.string().optional().or(z.literal('')),
  // ALTO-01: validação reforçada para prevenir XSS via javascript: URI.
  // Apenas YouTube e Vimeo são aceitos, protocolo https obrigatório.
  video_intro_url: z
    .string()
    .url('URL inválida')
    .refine(
      (url) => {
        try {
          const parsed = new URL(url)
          const allowedHosts = [
            'youtube.com',
            'www.youtube.com',
            'youtu.be',
            'vimeo.com',
            'www.vimeo.com',
          ]
          return parsed.protocol === 'https:' && allowedHosts.some((h) => parsed.hostname === h)
        } catch {
          return false
        }
      },
      { message: 'Apenas URLs de YouTube ou Vimeo sao aceitas (https://)' },
    )
    .optional()
    .or(z.literal('')),
  highlights: z.string().max(500, 'Máximo 500 caracteres').optional().or(z.literal('')),
})

const vehicleSchema = z.object({
  vehicle_model: z.string().min(2, 'Informe o modelo'),
  vehicle_year: z.coerce.number().int().min(1990).max(2030),
  vehicle_plate: z
    .string()
    .regex(
      /^[A-Z]{3}[-]?\d{1}[A-Z0-9]{1}\d{2}$|^[A-Z]{3}[-]?\d{4}$/,
      'Placa inválida. Use ABC1234 ou ABC1D23',
    ),
})

type PersonalFormData = z.infer<typeof personalSchema>
type VehicleFormData = z.infer<typeof vehicleSchema>

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DayAvail {
  enabled: boolean
  start_time: string
  end_time: string
}

type WeekAvail = Record<number, DayAvail>

const defaultWeekAvail: WeekAvail = Object.fromEntries(
  [0, 1, 2, 3, 4, 5, 6].map((d) => [d, { enabled: false, start_time: '08:00', end_time: '18:00' }]),
)

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const LANGUAGE_OPTIONS = [
  { code: 'pt', label: 'Português' },
  { code: 'en', label: 'Inglês' },
  { code: 'es', label: 'Espanhol' },
  { code: 'fr', label: 'Francês' },
  { code: 'de', label: 'Alemão' },
  { code: 'it', label: 'Italiano' },
  { code: 'ja', label: 'Japonês' },
  { code: 'zh', label: 'Mandarim' },
]

// ---------------------------------------------------------------------------
// Avatar Upload
// ---------------------------------------------------------------------------

function AvatarUpload({
  currentUrl,
  userId,
  onUploaded,
}: {
  currentUrl: string | null
  userId: string
  onUploaded: (url: string) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setUploading(true)
    const ext = file.name.split('.').pop()
    const path = `avatars/${userId}.${ext}`
    const { error: upErr } = await supabase.storage
      .from('profiles')
      .upload(path, file, { upsert: true })
    if (upErr) {
      toast.error('Erro ao enviar avatar.')
      setUploading(false)
      return
    }
    const { data } = supabase.storage.from('profiles').getPublicUrl(path)
    onUploaded(data.publicUrl)
    setUploading(false)
  }

  return (
    <div className="flex items-center gap-4">
      <div className="relative">
        {currentUrl ? (
          <img
            src={currentUrl}
            alt="Avatar"
            className="h-20 w-20 rounded-full object-cover border-2 border-teal-200"
          />
        ) : (
          <div className="h-20 w-20 rounded-full bg-teal-100 flex items-center justify-center text-teal-600">
            <Camera className="h-8 w-8" />
          </div>
        )}
      </div>
      <div className="space-y-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={uploading}
          onClick={() => inputRef.current?.click()}
          className="gap-1.5"
        >
          <Upload className="h-3.5 w-3.5" />
          {uploading ? 'Enviando...' : 'Alterar foto'}
        </Button>
        <p className="text-xs text-muted-foreground">JPG, PNG — máx. 2MB</p>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Vehicle Photos
// ---------------------------------------------------------------------------

function VehiclePhotos({
  photos,
  instructorId,
  onChange,
}: {
  photos: string[]
  instructorId: string
  onChange: (photos: string[]) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  async function handleFiles(e: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? [])
    if (photos.length + files.length > 4) {
      toast.warning('Máximo de 4 fotos de veículo.')
      return
    }
    setUploading(true)
    const newUrls: string[] = []
    for (const file of files) {
      const ext = file.name.split('.').pop()
      const path = `vehicles/${instructorId}/${Date.now()}.${ext}`
      const { error } = await supabase.storage
        .from('instructors')
        .upload(path, file, { upsert: false })
      if (!error) {
        const { data } = supabase.storage.from('instructors').getPublicUrl(path)
        newUrls.push(data.publicUrl)
      }
    }
    onChange([...photos, ...newUrls])
    setUploading(false)
    if (e.target) e.target.value = ''
  }

  function removePhoto(url: string) {
    onChange(photos.filter((p) => p !== url))
  }

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {photos.map((url, i) => (
          <div key={i} className="relative group">
            <img
              src={url}
              alt={`Foto ${i + 1}`}
              className="h-28 w-full object-cover rounded-lg border"
            />
            <button
              type="button"
              onClick={() => removePhoto(url)}
              className="absolute top-1 right-1 rounded-full bg-black/60 p-0.5 text-white opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
        {photos.length < 4 && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="h-28 rounded-lg border-2 border-dashed border-gray-200 flex flex-col items-center justify-center gap-1 text-muted-foreground hover:border-teal-400 hover:text-teal-600 transition-colors"
          >
            <Upload className="h-5 w-5" />
            <span className="text-xs">{uploading ? 'Enviando...' : 'Adicionar'}</span>
          </button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Até 4 fotos do veículo utilizado nas aulas.
      </p>
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function InstructorEditProfile() {
  const { user, profile, refreshProfile } = useAuth()

  const [instructor, setInstructor] = useState<InstructorRow | null>(null)
  const [loading, setLoading] = useState(true)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null)

  // Vehicle photos state
  const [vehiclePhotos, setVehiclePhotos] = useState<string[]>([])
  const [savingVehicle, setSavingVehicle] = useState(false)

  // Availability state
  const [weekAvail, setWeekAvail] = useState<WeekAvail>({ ...defaultWeekAvail })
  const [savingAvail, setSavingAvail] = useState(false)

  // Specialties state
  const [selectedSpecialties, setSelectedSpecialties] = useState<Set<SpecialtyType>>(new Set())
  const [savingSpecialties, setSavingSpecialties] = useState(false)

  // L1 enhanced fields
  const [certifications, setCertifications] = useState<string[]>([])
  const [newCertification, setNewCertification] = useState('')
  const [selectedLanguages, setSelectedLanguages] = useState<Set<string>>(new Set())

  // Social state
  const [whatsapp, setWhatsapp] = useState('')
  const [instagramUrl, setInstagramUrl] = useState('')
  const [linkedinUrl, setLinkedinUrl] = useState('')
  const [savingSocial, setSavingSocial] = useState(false)

  // Announcement state
  const [announcementText, setAnnouncementText] = useState('')
  const [announcementUntil, setAnnouncementUntil] = useState('')
  const [announcementId, setAnnouncementId] = useState<string | null>(null)
  const [savingAnnouncement, setSavingAnnouncement] = useState(false)

  // Feed post state
  const [postContent, setPostContent] = useState('')
  const [postImageFile, setPostImageFile] = useState<File | null>(null)
  const [postImagePreview, setPostImagePreview] = useState<string | null>(null)
  const [savingPost, setSavingPost] = useState(false)
  const [posts, setPosts] = useState<{ id: string; content: string; image_url: string | null; created_at: string }[]>([])

  // ── Personal form ──────────────────────────────────────────────────────────

  const {
    register: regPersonal,
    handleSubmit: handlePersonal,
    reset: resetPersonal,
    control: controlPersonal,
    watch: watchPersonal,
    formState: { errors: personalErrors, isSubmitting: personalSubmitting },
  } = useForm<PersonalFormData>({ resolver: zodResolver(personalSchema) })

  // ── Vehicle form ───────────────────────────────────────────────────────────

  const {
    register: regVehicle,
    handleSubmit: handleVehicle,
    reset: resetVehicle,
    formState: { errors: vehicleErrors },
  } = useForm<VehicleFormData>({ resolver: zodResolver(vehicleSchema) })

  // ── Load data ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!user?.id) return

    async function load() {
      setLoading(true)
      const { data: inst } = await supabase
        .from('instructors')
        .select('*')
        .eq('profile_id', user!.id)
        .single()

      if (inst) {
        setInstructor(inst as InstructorRow)
        setVehiclePhotos(inst.vehicle_photos ?? [])

        resetVehicle({
          vehicle_model: inst.vehicle_model ?? '',
          vehicle_year: inst.vehicle_year ?? new Date().getFullYear(),
          vehicle_plate: inst.vehicle_plate ?? '',
        })

        // Load availability
        const { data: avRows } = await supabase
          .from('availability')
          .select('*')
          .eq('instructor_id', inst.id)

        const merged: WeekAvail = { ...defaultWeekAvail }
        for (const row of (avRows ?? []) as AvailabilityRow[]) {
          merged[row.day_of_week] = {
            enabled: true,
            start_time: row.start_time.slice(0, 5),
            end_time: row.end_time.slice(0, 5),
          }
        }
        setWeekAvail(merged)

        // Load specialties
        const { data: specRows } = await supabase
          .from('instructor_specialties')
          .select('specialty')
          .eq('instructor_id', inst.id)

        setSelectedSpecialties(
          new Set((specRows ?? []).map((r: { specialty: SpecialtyType }) => r.specialty)),
        )

        setWhatsapp((inst as any).whatsapp ?? '')
        setInstagramUrl((inst as any).instagram_url ?? '')
        setLinkedinUrl((inst as any).linkedin_url ?? '')

        // L1 enhanced fields
        setCertifications((inst as any).certifications ?? [])
        setSelectedLanguages(new Set((inst as any).languages ?? []))

        // Load active announcement
        const { data: annRow } = await supabase
          .from('instructor_announcements')
          .select('id, text, active_until')
          .eq('instructor_id', inst.id)
          .or('active_until.is.null,active_until.gt.' + new Date().toISOString())
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle()

        if (annRow) {
          setAnnouncementId(annRow.id as string)
          setAnnouncementText((annRow as any).text ?? '')
          setAnnouncementUntil((annRow as any).active_until?.slice(0, 10) ?? '')
        }

        // Load recent posts
        const { data: postRows } = await supabase
          .from('instructor_posts')
          .select('id, content, image_url, created_at')
          .eq('instructor_id', inst.id)
          .order('created_at', { ascending: false })
          .limit(10)

        setPosts((postRows ?? []) as { id: string; content: string; image_url: string | null; created_at: string }[])
      }

      setAvatarUrl(profile?.avatar_url ?? null)
      resetPersonal({
        full_name: profile?.full_name ?? '',
        phone: profile?.phone ?? '',
        bio: inst?.bio ?? '',
        years_experience: inst?.years_experience ?? 1,
        hourly_rate: inst?.hourly_rate ?? 80,
        service_radius_km: inst?.service_radius_km ?? 20,
        city: profile?.city ?? inst?.latitude?.toString() ?? '',
        state: profile?.state ?? '',
        latitude: inst?.latitude ?? '',
        longitude: inst?.longitude ?? '',
        teaching_since: (inst as any)?.teaching_since ?? '',
        video_intro_url: (inst as any)?.video_intro_url ?? '',
        highlights: (inst as any)?.highlights ?? '',
      } as PersonalFormData)

      setLoading(false)
    }

    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, profile])

  // ── Submit: personal ───────────────────────────────────────────────────────

  async function onSavePersonal(data: PersonalFormData) {
    if (!user?.id || !instructor?.id) return

    const [profileRes, instructorRes] = await Promise.all([
      supabase
        .from('profiles')
        .update({
          full_name: data.full_name,
          phone: data.phone || null,
          city: data.city,
          state: data.state,
          avatar_url: avatarUrl,
        })
        .eq('id', user.id),
      supabase
        .from('instructors')
        .update({
          bio: data.bio,
          years_experience: data.years_experience,
          hourly_rate: data.hourly_rate,
          service_radius_km: data.service_radius_km,
          latitude: data.latitude !== '' ? Number(data.latitude) : null,
          longitude: data.longitude !== '' ? Number(data.longitude) : null,
          // Campos L1
          teaching_since: data.teaching_since || null,
          video_intro_url: data.video_intro_url || null,
          highlights: data.highlights || null,
          certifications,
          languages: Array.from(selectedLanguages),
        })
        .eq('id', instructor.id),
    ])

    if (profileRes.error || instructorRes.error) {
      toast.error('Erro ao salvar perfil.')
    } else {
      toast.success('Perfil atualizado com sucesso!')
      refreshProfile()
    }
  }

  // ── Submit: vehicle ────────────────────────────────────────────────────────

  async function onSaveVehicle(data: VehicleFormData) {
    if (!instructor?.id) return
    setSavingVehicle(true)
    const { error } = await supabase
      .from('instructors')
      .update({
        vehicle_model: data.vehicle_model,
        vehicle_year: data.vehicle_year,
        vehicle_plate: data.vehicle_plate.toUpperCase(),
        vehicle_photos: vehiclePhotos,
      })
      .eq('id', instructor.id)
    setSavingVehicle(false)
    if (error) {
      toast.error('Erro ao salvar veículo.')
    } else {
      toast.success('Dados do veículo salvos!')
    }
  }

  // ── Submit: availability ───────────────────────────────────────────────────

  async function onSaveAvailability() {
    if (!instructor?.id) return
    setSavingAvail(true)
    await supabase.from('availability').delete().eq('instructor_id', instructor.id)

    const inserts = Object.entries(weekAvail)
      .filter(([, v]) => v.enabled)
      .map(([day, v]) => ({
        instructor_id: instructor.id,
        day_of_week: Number(day),
        start_time: v.start_time + ':00',
        end_time: v.end_time + ':00',
      }))

    if (inserts.length > 0) {
      const { error } = await supabase.from('availability').insert(inserts)
      if (error) {
        toast.error('Erro ao salvar disponibilidade.')
        setSavingAvail(false)
        return
      }
    }

    toast.success('Disponibilidade salva!')
    setSavingAvail(false)
  }

  // ── Submit: specialties ────────────────────────────────────────────────────

  async function onSaveSpecialties() {
    if (!instructor?.id) return
    setSavingSpecialties(true)

    await supabase
      .from('instructor_specialties')
      .delete()
      .eq('instructor_id', instructor.id)

    const inserts = Array.from(selectedSpecialties).map((s) => ({
      instructor_id: instructor.id,
      specialty: s,
    }))

    if (inserts.length > 0) {
      const { error } = await supabase.from('instructor_specialties').insert(inserts)
      if (error) {
        toast.error('Erro ao salvar especialidades.')
        setSavingSpecialties(false)
        return
      }
    }

    toast.success('Especialidades salvas!')
    setSavingSpecialties(false)
  }

  // ── Submit: social links ───────────────────────────────────────────────────

  async function onSaveSocial() {
    if (!instructor?.id) return
    setSavingSocial(true)
    try {
      const { error } = await supabase
        .from('instructors')
        .update({
          whatsapp: whatsapp.trim() || null,
          instagram_url: instagramUrl.trim() || null,
          linkedin_url: linkedinUrl.trim() || null,
        })
        .eq('id', instructor.id)
      if (error) throw error
      toast.success('Links sociais salvos!')
    } catch {
      toast.error('Erro ao salvar links.')
    } finally {
      setSavingSocial(false)
    }
  }

  // ── Submit: announcement ──────────────────────────────────────────────────

  async function onSaveAnnouncement() {
    if (!instructor?.id) return
    setSavingAnnouncement(true)
    try {
      const payload = {
        instructor_id: instructor.id,
        text: announcementText.trim().slice(0, 200),
        active_until: announcementUntil ? new Date(announcementUntil + 'T23:59:59').toISOString() : null,
      }
      if (announcementId) {
        const { error } = await supabase
          .from('instructor_announcements')
          .update({ text: payload.text, active_until: payload.active_until })
          .eq('id', announcementId)
        if (error) throw error
      } else {
        const { data, error } = await supabase
          .from('instructor_announcements')
          .insert(payload)
          .select('id')
          .single()
        if (error) throw error
        setAnnouncementId((data as { id: string }).id)
      }
      toast.success('Anúncio salvo!')
    } catch {
      toast.error('Erro ao salvar anúncio.')
    } finally {
      setSavingAnnouncement(false)
    }
  }

  async function onDeleteAnnouncement() {
    if (!instructor?.id || !announcementId) return
    setSavingAnnouncement(true)
    try {
      const { error } = await supabase
        .from('instructor_announcements')
        .delete()
        .eq('id', announcementId)
      if (error) throw error
      setAnnouncementId(null)
      setAnnouncementText('')
      setAnnouncementUntil('')
      toast.success('Anúncio removido.')
    } catch {
      toast.error('Erro ao remover anúncio.')
    } finally {
      setSavingAnnouncement(false)
    }
  }

  // ── Submit: feed post ─────────────────────────────────────────────────────

  async function onPublishPost() {
    if (!instructor?.id || !postContent.trim()) return
    setSavingPost(true)
    try {
      let imageUrl: string | null = null

      if (postImageFile) {
        const ext = postImageFile.name.split('.').pop() ?? 'jpg'
        const path = `${instructor.id}/${Date.now()}.${ext}`
        const { error: upErr } = await supabase.storage
          .from('instructor-posts')
          .upload(path, postImageFile, { upsert: false })
        if (upErr) throw upErr
        const { data: urlData } = supabase.storage.from('instructor-posts').getPublicUrl(path)
        imageUrl = urlData.publicUrl
      }

      const { data, error } = await supabase
        .from('instructor_posts')
        .insert({
          instructor_id: instructor.id,
          content: postContent.trim().slice(0, 500),
          image_url: imageUrl,
        })
        .select('id, content, image_url, created_at')
        .single()

      if (error) throw error

      setPosts((prev) => [data as { id: string; content: string; image_url: string | null; created_at: string }, ...prev])
      setPostContent('')
      setPostImageFile(null)
      setPostImagePreview(null)
      toast.success('Post publicado!')
    } catch {
      toast.error('Erro ao publicar post.')
    } finally {
      setSavingPost(false)
    }
  }

  async function onDeletePost(postId: string) {
    if (!instructor?.id) return
    try {
      await supabase.from('instructor_posts').delete().eq('id', postId)
      setPosts((prev) => prev.filter((p) => p.id !== postId))
      toast.success('Post removido.')
    } catch {
      toast.error('Erro ao remover post.')
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Editar perfil</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Mantenha suas informações atualizadas para atrair mais alunos.
        </p>
      </div>

      <Tabs defaultValue="personal">
        <TabsList className="grid w-full grid-cols-3 sm:grid-cols-9 mb-6">
          <TabsTrigger value="personal">Informações</TabsTrigger>
          <TabsTrigger value="vehicle">Veículo</TabsTrigger>
          <TabsTrigger value="availability">Agenda</TabsTrigger>
          <TabsTrigger value="disponibilidade">Disponibilidade</TabsTrigger>
          <TabsTrigger value="specialties">Especialidades</TabsTrigger>
          <TabsTrigger value="social">Contato</TabsTrigger>
          <TabsTrigger value="comunicacao">Comunicação</TabsTrigger>
          <TabsTrigger value="privacidade">Privacidade</TabsTrigger>
          <TabsTrigger value="seguranca">Segurança</TabsTrigger>
        </TabsList>

        {/* ── Tab 1: Informações pessoais ─────────────────────────────────── */}
        <TabsContent value="personal">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Informações pessoais</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePersonal(onSavePersonal)} className="space-y-5">
                {/* Avatar */}
                <div className="space-y-1.5">
                  <Label>Foto de perfil</Label>
                  {user && (
                    <AvatarUpload
                      currentUrl={avatarUrl}
                      userId={user.id}
                      onUploaded={setAvatarUrl}
                    />
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="full_name">Nome completo</Label>
                    <Input id="full_name" {...regPersonal('full_name')} />
                    {personalErrors.full_name && (
                      <p className="text-xs text-red-500">{personalErrors.full_name.message}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="phone">Telefone</Label>
                    <Input id="phone" type="tel" {...regPersonal('phone')} placeholder="(11) 9 9999-9999" />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="bio">Biografia</Label>
                  <Textarea
                    id="bio"
                    rows={4}
                    {...regPersonal('bio')}
                    placeholder="Conte sobre você, sua experiência e metodologia..."
                  />
                  {personalErrors.bio && (
                    <p className="text-xs text-red-500">{personalErrors.bio.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="years_experience">Anos de experiência</Label>
                    <Input
                      id="years_experience"
                      type="number"
                      min={0}
                      max={60}
                      {...regPersonal('years_experience')}
                    />
                    {personalErrors.years_experience && (
                      <p className="text-xs text-red-500">{personalErrors.years_experience.message}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="hourly_rate">Valor por hora (R$)</Label>
                    <Input
                      id="hourly_rate"
                      type="number"
                      min={20}
                      max={500}
                      step={5}
                      {...regPersonal('hourly_rate')}
                    />
                    {personalErrors.hourly_rate ? (
                      <p className="text-xs text-red-500">{personalErrors.hourly_rate.message}</p>
                    ) : (
                      <p className="text-xs text-muted-foreground">
                        Você recebe este valor inteiro. O aluno verá{' '}
                        {formatCurrency(getStudentPrice(Number(watchPersonal('hourly_rate')) || 0))}/h
                        (+{PLATFORM_FEE_PERCENT}% da plataforma).
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="service_radius_km">Raio de atendimento (km)</Label>
                    <Input
                      id="service_radius_km"
                      type="number"
                      min={5}
                      max={100}
                      {...regPersonal('service_radius_km')}
                    />
                    {personalErrors.service_radius_km && (
                      <p className="text-xs text-red-500">{personalErrors.service_radius_km.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="city">Cidade</Label>
                    <Input id="city" {...regPersonal('city')} />
                    {personalErrors.city && (
                      <p className="text-xs text-red-500">{personalErrors.city.message}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="state">Estado</Label>
                    <select
                      id="state"
                      {...regPersonal('state')}
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs focus:outline-none focus:ring-1 focus:ring-ring"
                    >
                      <option value="">Selecione</option>
                      {BRAZILIAN_STATES.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                    {personalErrors.state && (
                      <p className="text-xs text-red-500">{personalErrors.state.message}</p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="latitude">Latitude (opcional)</Label>
                    <Input
                      id="latitude"
                      type="number"
                      step="any"
                      placeholder="-23.5505"
                      {...regPersonal('latitude')}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="longitude">Longitude (opcional)</Label>
                    <Input
                      id="longitude"
                      type="number"
                      step="any"
                      placeholder="-46.6333"
                      {...regPersonal('longitude')}
                    />
                  </div>
                </div>

                {/* ── L1: Campos aprimorados ──────────────────────────────── */}
                <div className="border-t pt-4 space-y-4">
                  <p className="text-sm font-semibold text-gray-700">Informações complementares</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="teaching_since">Instrutor desde</Label>
                      <Input
                        id="teaching_since"
                        type="date"
                        {...regPersonal('teaching_since')}
                      />
                      <p className="text-xs text-muted-foreground">Data em que começou a dar aulas.</p>
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="video_intro_url">URL do vídeo de apresentação</Label>
                      <Input
                        id="video_intro_url"
                        type="url"
                        placeholder="https://youtube.com/watch?v=..."
                        {...regPersonal('video_intro_url')}
                      />
                      {personalErrors.video_intro_url && (
                        <p className="text-xs text-red-500">{personalErrors.video_intro_url.message}</p>
                      )}
                      <p className="text-xs text-muted-foreground">YouTube ou Vimeo. Exibido como link externo.</p>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="highlights">Destaques (bio complementar)</Label>
                    <Controller
                      name="highlights"
                      control={controlPersonal}
                      render={({ field }) => (
                        <>
                          <Textarea
                            id="highlights"
                            rows={3}
                            maxLength={500}
                            placeholder="Diferenciais, conquistas, metodologia especial..."
                            {...field}
                            value={field.value ?? ''}
                          />
                          <p className="text-xs text-muted-foreground text-right">
                            {(field.value ?? '').length}/500
                          </p>
                        </>
                      )}
                    />
                    {personalErrors.highlights && (
                      <p className="text-xs text-red-500">{personalErrors.highlights.message}</p>
                    )}
                  </div>

                  {/* Idiomas */}
                  <div className="space-y-2">
                    <Label>Idiomas</Label>
                    <div className="flex flex-wrap gap-2">
                      {LANGUAGE_OPTIONS.map(({ code, label }) => {
                        const checked = selectedLanguages.has(code)
                        return (
                          <button
                            key={code}
                            type="button"
                            onClick={() =>
                              setSelectedLanguages((prev) => {
                                const next = new Set(prev)
                                if (next.has(code)) next.delete(code)
                                else next.add(code)
                                return next
                              })
                            }
                            className={cn(
                              'px-3 py-1.5 rounded-full text-xs font-medium border transition-all',
                              checked
                                ? 'border-teal-500 bg-teal-50 text-teal-800'
                                : 'border-gray-200 text-gray-600 hover:border-gray-300',
                            )}
                          >
                            {label}
                          </button>
                        )
                      })}
                    </div>
                  </div>

                  {/* Certificações */}
                  <div className="space-y-2">
                    <Label>Certificações</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Ex: DETRAN-SP #12345"
                        value={newCertification}
                        onChange={(e) => setNewCertification(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            const v = newCertification.trim()
                            if (v && !certifications.includes(v)) {
                              setCertifications((prev) => [...prev, v])
                            }
                            setNewCertification('')
                          }
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          const v = newCertification.trim()
                          if (v && !certifications.includes(v)) {
                            setCertifications((prev) => [...prev, v])
                          }
                          setNewCertification('')
                        }}
                      >
                        Adicionar
                      </Button>
                    </div>
                    {certifications.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {certifications.map((cert, i) => (
                          <span
                            key={i}
                            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gray-100 text-xs text-gray-700"
                          >
                            {cert}
                            <button
                              type="button"
                              onClick={() =>
                                setCertifications((prev) => prev.filter((_, idx) => idx !== i))
                              }
                              className="ml-1 text-gray-400 hover:text-red-500 transition-colors"
                              aria-label={`Remover ${cert}`}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Pressione Enter ou clique em Adicionar. Exemplos: "DETRAN-SP #12345", "CFP Avançado".
                    </p>
                  </div>
                </div>

                <div className="flex justify-end pt-2">
                  <Button type="submit" disabled={personalSubmitting} className="gap-1.5">
                    <Save className="h-4 w-4" />
                    {personalSubmitting ? 'Salvando...' : 'Salvar alterações'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <div className="mt-4 space-y-4">
            <InstructorCnhUploadCard />
            <InstructorIdentityCard />
          </div>
        </TabsContent>

        {/* ── Tab 2: Veículo ───────────────────────────────────────────────── */}
        <TabsContent value="vehicle">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Informações do veículo</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleVehicle(onSaveVehicle)} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="vehicle_model">Modelo</Label>
                    <Input id="vehicle_model" {...regVehicle('vehicle_model')} placeholder="Ex: Fiat Argo" />
                    {vehicleErrors.vehicle_model && (
                      <p className="text-xs text-red-500">{vehicleErrors.vehicle_model.message}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="vehicle_year">Ano</Label>
                    <Input
                      id="vehicle_year"
                      type="number"
                      min={1990}
                      max={2030}
                      {...regVehicle('vehicle_year')}
                    />
                    {vehicleErrors.vehicle_year && (
                      <p className="text-xs text-red-500">{vehicleErrors.vehicle_year.message}</p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="vehicle_plate">Placa</Label>
                    <Input
                      id="vehicle_plate"
                      {...regVehicle('vehicle_plate')}
                      placeholder="ABC1234"
                      className="uppercase"
                      onChange={(e) => {
                        e.target.value = e.target.value.toUpperCase()
                      }}
                    />
                    {vehicleErrors.vehicle_plate && (
                      <p className="text-xs text-red-500">{vehicleErrors.vehicle_plate.message}</p>
                    )}
                  </div>
                </div>

                {/* Vehicle photos */}
                <div className="space-y-1.5">
                  <Label>Fotos do veículo</Label>
                  {instructor && (
                    <VehiclePhotos
                      photos={vehiclePhotos}
                      instructorId={instructor.id}
                      onChange={setVehiclePhotos}
                    />
                  )}
                </div>

                <div className="flex justify-end pt-2">
                  <Button type="submit" disabled={savingVehicle} className="gap-1.5">
                    <Save className="h-4 w-4" />
                    {savingVehicle ? 'Salvando...' : 'Salvar veículo'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab 3: Disponibilidade ───────────────────────────────────────── */}
        <TabsContent value="availability">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Disponibilidade semanal</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {[1, 2, 3, 4, 5, 6, 0].map((dow) => {
                const avail = weekAvail[dow]
                return (
                  <div key={dow} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="font-medium">{DAY_OF_WEEK_LABELS[dow]}</Label>
                      <Switch
                        checked={avail.enabled}
                        onCheckedChange={(checked) =>
                          setWeekAvail((prev) => ({
                            ...prev,
                            [dow]: { ...prev[dow], enabled: checked },
                          }))
                        }
                      />
                    </div>
                    {avail.enabled && (
                      <div className="flex items-center gap-3 pl-2">
                        <div className="flex items-center gap-2">
                          <Label className="text-sm text-muted-foreground w-10">De</Label>
                          <Input
                            type="time"
                            value={avail.start_time}
                            onChange={(e) =>
                              setWeekAvail((prev) => ({
                                ...prev,
                                [dow]: { ...prev[dow], start_time: e.target.value },
                              }))
                            }
                            className="h-8 w-32 text-sm"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <Label className="text-sm text-muted-foreground w-6">às</Label>
                          <Input
                            type="time"
                            value={avail.end_time}
                            onChange={(e) =>
                              setWeekAvail((prev) => ({
                                ...prev,
                                [dow]: { ...prev[dow], end_time: e.target.value },
                              }))
                            }
                            className="h-8 w-32 text-sm"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}

              <div className="flex justify-end pt-2 border-t">
                <Button
                  type="button"
                  onClick={onSaveAvailability}
                  disabled={savingAvail}
                  className="gap-1.5"
                >
                  <Save className="h-4 w-4" />
                  {savingAvail ? 'Salvando...' : 'Salvar disponibilidade'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab 4: Especialidades ────────────────────────────────────────── */}
        <TabsContent value="specialties">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Especialidades</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Selecione as especialidades que você oferece. Isso ajuda alunos a encontrar o instrutor certo.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(Object.entries(SPECIALTY_LABELS) as [SpecialtyType, string][]).map(
                  ([key, label]) => {
                    const checked = selectedSpecialties.has(key)
                    return (
                      <div
                        key={key}
                        className={cn(
                          'rounded-lg border-2 p-3 transition-colors',
                          checked
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-gray-200',
                        )}
                      >
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              setSelectedSpecialties((prev) => {
                                const next = new Set(prev)
                                if (e.target.checked) next.add(key)
                                else next.delete(key)
                                return next
                              })
                            }}
                            className="rounded text-teal-600"
                          />
                          <span className={cn('text-sm font-medium', checked ? 'text-teal-800' : 'text-gray-700')}>
                            {label}
                          </span>
                        </label>
                      </div>
                    )
                  },
                )}
              </div>

              {/* O "preço diferenciado" por especialidade ficava só no navegador e nunca
                  mudava a cobrança (removido em 2026-10-12). Preço diferente = pacote. */}
              <p className="text-xs text-muted-foreground">
                Quer cobrar diferente por um tipo de aula? Crie um pacote em{' '}
                <Link to="/instrutor/pacotes" className="text-teal-700 underline">Pacotes</Link>.
              </p>

              {selectedSpecialties.size === 0 && (
                <p className="text-xs text-red-500">
                  Selecione pelo menos uma especialidade.
                </p>
              )}

              <div className="flex justify-end pt-2 border-t">
                <Button
                  type="button"
                  onClick={onSaveSpecialties}
                  disabled={savingSpecialties || selectedSpecialties.size === 0}
                  className="gap-1.5"
                >
                  <Save className="h-4 w-4" />
                  {savingSpecialties ? 'Salvando...' : 'Salvar especialidades'}
                </Button>
              </div>

              {/* Batch U3 — Tags de especialidade */}
              {instructor?.id && (
                <InstructorTagsEditor instructorId={instructor.id} />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Tab 5: Contato / Redes sociais ────────────────────────────── */}
        <TabsContent value="social">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Contato e Redes Sociais</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <p className="text-sm text-muted-foreground">
                Estas informações ficarão visíveis no seu perfil público para facilitar o contato com alunos.
              </p>

              <div className="space-y-1.5">
                <Label htmlFor="whatsapp" className="flex items-center gap-2">
                  <MessageCircle className="h-4 w-4 text-green-600" />
                  WhatsApp
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-sm text-muted-foreground">+55</span>
                  <Input
                    id="whatsapp"
                    placeholder="(11) 9 9999-9999"
                    value={whatsapp}
                    onChange={(e) => setWhatsapp(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Número para contato via WhatsApp. Será exibido um botão "Chamar no WhatsApp" no seu perfil.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="instagram" className="flex items-center gap-2">
                  <Instagram className="h-4 w-4 text-pink-500" />
                  Instagram
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-sm text-muted-foreground">@</span>
                  <Input
                    id="instagram"
                    placeholder="seuperfil"
                    value={instagramUrl.replace(/^https?:\/\/(www\.)?instagram\.com\/?/, '').replace(/^@/, '')}
                    onChange={(e) => {
                      const v = e.target.value.trim().replace(/^@/, '')
                      setInstagramUrl(v ? `https://instagram.com/${v}` : '')
                    }}
                    className="pl-8"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="linkedin" className="flex items-center gap-2">
                  <Linkedin className="h-4 w-4 text-blue-600" />
                  LinkedIn
                </Label>
                <div className="relative">
                  <LinkIcon className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="linkedin"
                    placeholder="linkedin.com/in/seuperfil"
                    value={linkedinUrl}
                    onChange={(e) => setLinkedinUrl(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-2 border-t">
                <Button
                  type="button"
                  onClick={onSaveSocial}
                  disabled={savingSocial}
                  className="gap-1.5"
                >
                  <Save className="h-4 w-4" />
                  {savingSocial ? 'Salvando...' : 'Salvar links'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        {/* ── Tab 6: Comunicação ──────────────────────────────────────────── */}
        <TabsContent value="comunicacao">
          <div className="space-y-6">

            {/* Push notifications */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Bell className="h-4 w-4 text-brand-600" />
                  Notificações push
                </CardTitle>
              </CardHeader>
              <CardContent>
                <PushNotificationToggle
                  label="Receber alertas de agendamentos e mensagens"
                />
              </CardContent>
            </Card>

            {/* Anúncio fixado */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Megaphone className="h-4 w-4 text-amber-600" />
                  Anúncio no perfil
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Um anúncio fixado aparece no topo do seu perfil público. Use para avisar sobre férias,
                  promoções ou novidades. Máximo 200 caracteres.
                </p>

                <div className="space-y-1.5">
                  <Label htmlFor="announcement-text">Texto do anúncio</Label>
                  <Textarea
                    id="announcement-text"
                    rows={3}
                    maxLength={200}
                    placeholder="Ex: Estou de volta após as férias! Agenda aberta para julho."
                    value={announcementText}
                    onChange={(e) => setAnnouncementText(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground text-right">
                    {announcementText.length}/200
                  </p>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="announcement-until">Validade (opcional)</Label>
                  <input
                    id="announcement-until"
                    type="date"
                    value={announcementUntil}
                    min={new Date().toISOString().slice(0, 10)}
                    onChange={(e) => setAnnouncementUntil(e.target.value)}
                    className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  />
                  <p className="text-xs text-muted-foreground">
                    Deixe em branco para exibir indefinidamente.
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button
                    type="button"
                    onClick={onSaveAnnouncement}
                    disabled={savingAnnouncement || !announcementText.trim()}
                    className="gap-1.5"
                  >
                    <Save className="h-4 w-4" />
                    {savingAnnouncement ? 'Salvando...' : 'Salvar anúncio'}
                  </Button>
                  {announcementId && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onDeleteAnnouncement}
                      disabled={savingAnnouncement}
                      className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                      Remover
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Feed de posts */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Rss className="h-4 w-4 text-brand-600" />
                  Publicar novidade
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Posts curtos aparecem na aba "Novidades" do seu perfil público. Máximo 500 caracteres.
                </p>

                <div className="space-y-1.5">
                  <Label htmlFor="post-content">Texto</Label>
                  <Textarea
                    id="post-content"
                    rows={4}
                    maxLength={500}
                    placeholder="Compartilhe uma dica de direção, conquista de aluno ou novidade..."
                    value={postContent}
                    onChange={(e) => setPostContent(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground text-right">
                    {postContent.length}/500
                  </p>
                </div>

                {/* Foto opcional */}
                <div className="space-y-1.5">
                  <Label>Foto (opcional)</Label>
                  {postImagePreview ? (
                    <div className="relative inline-block">
                      <img
                        src={postImagePreview}
                        alt="Preview"
                        className="h-40 rounded-lg object-cover border"
                      />
                      <button
                        type="button"
                        onClick={() => { setPostImageFile(null); setPostImagePreview(null) }}
                        className="absolute top-1 right-1 rounded-full bg-black/60 p-0.5 text-white"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex items-center gap-2 cursor-pointer w-fit">
                      <div className="flex items-center gap-2 px-4 py-2 rounded-lg border border-dashed border-gray-300 hover:border-brand-400 text-sm text-muted-foreground hover:text-brand-600 transition-colors">
                        <Upload className="h-4 w-4" />
                        Adicionar foto
                      </div>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (!file) return
                          if (file.size > 5 * 1024 * 1024) {
                            toast.error('Imagem muito grande. Máximo 5 MB.')
                            return
                          }
                          setPostImageFile(file)
                          setPostImagePreview(URL.createObjectURL(file))
                        }}
                      />
                    </label>
                  )}
                </div>

                <Button
                  type="button"
                  onClick={onPublishPost}
                  disabled={savingPost || !postContent.trim()}
                  className="gap-1.5"
                >
                  <Rss className="h-4 w-4" />
                  {savingPost ? 'Publicando...' : 'Publicar'}
                </Button>

                {/* Lista de posts recentes */}
                {posts.length > 0 && (
                  <div className="pt-4 border-t space-y-3">
                    <p className="text-sm font-medium text-gray-700">Posts recentes</p>
                    {posts.map((post) => (
                      <div
                        key={post.id}
                        className="flex items-start gap-3 rounded-lg border border-gray-100 bg-gray-50 p-3"
                      >
                        {post.image_url && (
                          <img
                            src={post.image_url}
                            alt="Post"
                            className="h-12 w-12 rounded object-cover shrink-0"
                          />
                        )}
                        <p className="text-sm text-gray-700 flex-1 min-w-0 line-clamp-2">
                          {post.content}
                        </p>
                        <button
                          type="button"
                          onClick={() => onDeletePost(post.id)}
                          className="shrink-0 text-red-400 hover:text-red-600 transition-colors"
                          aria-label="Remover post"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

          </div>
        </TabsContent>

        {/* ── Tab: Privacidade / LGPD ──────────────────────────────────────── */}
        <TabsContent value="privacidade" className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <ShieldCheck className="h-5 w-5 text-brand-600" />
            <div>
              <h3 className="font-semibold">Privacidade e LGPD</h3>
              <p className="text-sm text-muted-foreground">
                Gerencie seus dados e solicite direitos previstos na LGPD.
              </p>
            </div>
          </div>
          <LgpdRequestForm />
        </TabsContent>

        {/* ── Tab 7: Segurança ─────────────────────────────────────────────── */}
        <TabsContent value="seguranca" className="space-y-4">
          <InstructorCnhUploadCard />
          <InstructorIdentityDocCard />
          <TwoFactorSetupCard />
        </TabsContent>

        {/* ── Tab 8: Disponibilidade (H1) ──────────────────────────────────── */}
        <TabsContent value="disponibilidade" className="space-y-5">
          <div className="flex items-center gap-2 mb-4">
            <CalendarClock className="h-5 w-5 text-primary" />
            <div>
              <h3 className="font-semibold">Horários disponíveis</h3>
              <p className="text-sm text-muted-foreground">
                Os dias e horários que os alunos podem reservar ficam na sua Agenda.
              </p>
            </div>
          </div>
          {/* Os editores que ficavam aqui (AvailabilityEditor/WeeklyAvailabilityEditor)
              gravavam em tabelas que a busca de horários não lê — o instrutor
              mudava e nada acontecia. A agenda real é a da página Agenda. */}
          <Button asChild variant="outline">
            <Link to="/instrutor/agenda">Abrir minha agenda</Link>
          </Button>

          {/* O1 — Modo férias */}
          <div className="flex items-center gap-2 mt-6 mb-2">
            <Palmtree className="h-5 w-5 text-teal-600" />
            <div>
              <h3 className="font-semibold">Férias</h3>
              <p className="text-sm text-muted-foreground">
                Bloqueie novos agendamentos durante seu período de descanso.
              </p>
            </div>
          </div>
          <VacationModeCard />
        </TabsContent>

      </Tabs>
    </div>
  )
}
